// ClassificationMenu/ClassificationMenu.jsx
// Chip-based classification picker for level, distribution, and compartments

import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import {CLASSIFICATION_LEVELS} from '../../helpers/classificationLevels';
import {COMPARTMENT_OPTIONS, DISTRIBUTION_OPTIONS} from '../../helpers/classificationOptions';
import {getMaxClassificationLevel} from '../../helpers/documentClassification';
import {useTopOptions} from './useClassificationOptions';
import ChipSection from './ChipSection';
import LevelChips from './LevelChips';

function ClassificationMenu({anchorEl, anchorPosition, open, onClose, value, onApply}) {
    const [search, setSearch] = useState('');
    const [showAllDistribution, setShowAllDistribution] = useState(false);
    const [showAllCompartment, setShowAllCompartment] = useState(false);
    const [level, setLevel] = useState('U');
    const [distribution, setDistribution] = useState([]);
    const [compartment, setCompartment] = useState([]);
    // Floor below which the level cannot be set — highest classification among the
    // block's own citations/references (see getBlockCitationsMaxLevel). 'U' when none.
    const [minLevel, setMinLevel] = useState('U');

    // Store the latest onApply without changing handler identities.
    const onApplyRef = useRef(onApply);
    useEffect(() => {
        onApplyRef.current = onApply;
    }, [onApply]);

    // Sync UI state from the external value when opening
    useEffect(() => {
        if (open && value) {
            setLevel(value.classificationLevel || 'U');
            setDistribution(value.distribution || []);
            setCompartment(value.compartment || []);
            setMinLevel(value.minLevel || 'U');
        }
    }, [open, value]);

    // Reset UI-only state when closing
    useEffect(() => {
        if (!open) {
            setSearch('');
            setShowAllDistribution(false);
            setShowAllCompartment(false);
        }
    }, [open]);

    const hasSearch = search.trim().length > 0;

    const distributionView = useTopOptions(
        DISTRIBUTION_OPTIONS,
        search,
        showAllDistribution,
        distribution
    );
    const compartmentView = useTopOptions(
        COMPARTMENT_OPTIONS,
        search,
        showAllCompartment,
        compartment
    );

    const handleLevelChange = useCallback((newLevel) => {
        // Guard against applying a level below the citation floor (chips below the
        // floor are disabled in LevelChips, but keep this as a defensive backstop).
        if (getMaxClassificationLevel(newLevel, minLevel) !== newLevel) return;
        setLevel(newLevel);
        if (onApplyRef.current) {
            onApplyRef.current({classificationLevel: newLevel, distribution, compartment});
        }
    }, [distribution, compartment, minLevel]);

    const handleDistributionToggle = useCallback((option) => {
        setDistribution(prev => {
            const newDistribution = prev.includes(option)
                ? prev.filter((item) => item !== option)
                : [...prev, option];

            if (onApplyRef.current) {
                onApplyRef.current({classificationLevel: level, distribution: newDistribution, compartment});
            }

            return newDistribution;
        });
    }, [level, compartment]);

    const handleCompartmentToggle = useCallback((option) => {
        setCompartment(prev => {
            const newCompartment = prev.includes(option)
                ? prev.filter((item) => item !== option)
                : [...prev, option];

            if (onApplyRef.current) {
                onApplyRef.current({classificationLevel: level, distribution, compartment: newCompartment});
            }

            return newCompartment;
        });
    }, [level, distribution]);

    const toggleShowAllDistribution = useCallback(() => {
        setShowAllDistribution(prev => !prev);
    }, []);

    const toggleShowAllCompartment = useCallback(() => {
        setShowAllCompartment(prev => !prev);
    }, []);

    // Lock position while open: always use the snapshot anchorPosition.
    const lockedAnchorPosition = anchorPosition
        ? {top: anchorPosition.top, left: anchorPosition.left}
        : null;

    return (
        <Popover
            disableRestoreFocus
            disableEnforceFocus
            disableAutoFocus
            open={open}
            anchorReference={lockedAnchorPosition ? 'anchorPosition' : 'anchorEl'}
            anchorPosition={lockedAnchorPosition || undefined}
            anchorEl={!lockedAnchorPosition ? anchorEl : undefined}
            onClose={onClose}
            anchorOrigin={{vertical: 'top', horizontal: 'left'}}
            transformOrigin={{vertical: 'top', horizontal: 'left'}}
            slotProps={{
                paper: {
                    sx: {
                        p: 0,
                        mt: "16px",
                        width: 420,
                        maxHeight: 520,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'divider',
                    }
                }
            }}
        >
            <Stack spacing={2} sx={{flex: 1, overflowY: 'auto', p: 2}}>
                <TextField
                    size="small"
                    placeholder="Search options"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    autoComplete={false}
                />
                {!hasSearch && (
                    <>
                        <div>
                            <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                                Classification Level
                            </Typography>
                            <LevelChips
                                levels={CLASSIFICATION_LEVELS}
                                selectedLevel={level}
                                minLevel={minLevel}
                                onLevelChange={handleLevelChange}
                            />
                        </div>
                        <Divider/>
                    </>
                )}
                <ChipSection
                    title="Distributions"
                    options={distributionView.display}
                    selected={distribution}
                    onToggle={handleDistributionToggle}
                    expanded={showAllDistribution}
                    hasMore={distributionView.hasMore}
                    canToggle={distributionView.canToggle}
                    toggleShowMore={toggleShowAllDistribution}
                />
                <Divider/>
                <ChipSection
                    title="Compartments"
                    options={compartmentView.display}
                    selected={compartment}
                    onToggle={handleCompartmentToggle}
                    expanded={showAllCompartment}
                    hasMore={compartmentView.hasMore}
                    canToggle={compartmentView.canToggle}
                    toggleShowMore={toggleShowAllCompartment}
                />
            </Stack>
        </Popover>
    );
}

ClassificationMenu.propTypes = {
    anchorEl: PropTypes.instanceOf(Element),
    anchorPosition: PropTypes.shape({
        top: PropTypes.number.isRequired,
        left: PropTypes.number.isRequired,
    }),
    open: PropTypes.bool,
    onClose: PropTypes.func,
    value: PropTypes.shape({
        classificationLevel: PropTypes.string,
        distribution: PropTypes.arrayOf(PropTypes.string),
        compartment: PropTypes.arrayOf(PropTypes.string),
        minLevel: PropTypes.string,
    }),
    onApply: PropTypes.func,
};

export default memo(ClassificationMenu);

